// src/features/tidsregistering/components/absence-planner/AbsencePlannerChart.jsx
import React, { useCallback, useEffect } from 'react';
import { Paper } from '@mui/material';
import {
  useGanttLogic,
  useExpandedNonWorkingDays,
  TimelineGrid,
  ROW_NAME_WIDTH,
} from '../../../../components/timeline';
import { GanttDndProvider } from '../../../../components/gantt/context/GanttDndContext';
import AbsenceRow from './AbsenceRow';
import AbsenceSidebarCell from './AbsenceSidebarCell';
import { useTranslation } from 'react-i18next';

/** Returns true if the Date is Saturday (6) or Sunday (0). */
function isWeekend(date) {
  const d = date.getDay();
  return d === 0 || d === 6;
}

/**
 * AbsencePlannerChart — day-granularity timeline for multi-day absences.
 *
 * Props:
 *   categories        — Category[] from absencesToCategories adapter
 *   baseDate          — Date   reference date (today by default)
 *   onAbsenceClick    — (slot) => void   called when a bar is clicked
 *   onDragCreate      — ({ category, startDate, endDate }) => void   drag-to-create
 *   onDateRangeChange — (start, end) => void   for data fetching on scroll
 *   height            — string  CSS height of the chart container
 */
export default function AbsencePlannerChart({
  categories = [],
  baseDate = new Date(),
  onAbsenceClick = null,
  onDragCreate = null,
  onDateRangeChange = null,
  height = '70vh',
  unavailabilityBlocks = [],
}) {
  const { t } = useTranslation(['common']);
  // Skip ghost-task generation — absences are not recurring RRule tasks
  const passThrough = useCallback((cats) => cats, []);

  const {
    scrollContainerRef,
    scrollState,
    handleScroll,
    categories: processedCategories,
    DAY_WIDTH,
    currentViewDateRange,
  } = useGanttLogic({
    categories,
    baseDate,
    onDateRangeChange,
    processCategories: passThrough,
  });

  // Non-working days (public holidays + closing days) come from a single unified
  // endpoint now, so no separate by-year public-holiday fetch is needed here.
  const { nonWorkingDaysMap, fetchExpandedNonWorkingDays, isNonWorkingDay, getNonWorkingDayNames } =
    useExpandedNonWorkingDays();

  // Fetch non-working days whenever the visible range changes
  useEffect(() => {
    if (currentViewDateRange?.startDate && currentViewDateRange?.endDate) {
      fetchExpandedNonWorkingDays(currentViewDateRange.startDate, currentViewDateRange.endDate);
    }
  }, [currentViewDateRange, fetchExpandedNonWorkingDays]);

  // Combined check: non-working day (holiday or closing day) OR weekend
  const isShadedDay = useCallback((date) => {
    if (isNonWorkingDay(date)) return true;
    return isWeekend(date);
  }, [isNonWorkingDay]);

  // Returns name(s) text for the overlay label
  const getShadedDayNames = useCallback((date) => {
    return getNonWorkingDayNames(date);
  }, [getNonWorkingDayNames]);

  // renderRows — substitutes AbsenceRow for CategoryRow
  const renderRows = useCallback(
    ({ categories: cats, visibleDayIndices, getDateForDayIndex, dayWidth, baseDate: bd }) =>
      cats.map((category) => (
        <AbsenceRow
          key={category.id}
          category={category}
          visibleDayIndices={visibleDayIndices}
          getDateForDayIndex={getDateForDayIndex}
          dayWidth={dayWidth}
          baseDate={bd}
          onTaskClick={onAbsenceClick}
          onDragCreate={onDragCreate}
          unavailabilityBlocks={unavailabilityBlocks.filter((b) => b.staff_member_id === category.id)}
        />
      )),
    [onAbsenceClick, onDragCreate, unavailabilityBlocks],
  );

  // renderLabel — substitutes AbsenceSidebarCell for CategoryLabel
  const renderLabel = useCallback(
    ({ category, categoryHeight, index }) => (
      <AbsenceSidebarCell
        key={category.id}
        category={category}
        categoryHeight={categoryHeight}
        index={index}
      />
    ),
    [],
  );

  return (
    <GanttDndProvider>
    <Paper sx={{ height, display: 'flex', overflow: 'hidden' }}>
      <TimelineGrid
        ref={scrollContainerRef}
        sidebarTitle={t('common:navigation.staff_members')}
        onScroll={handleScroll}
        scrollState={scrollState}
        categories={processedCategories}
        baseDate={baseDate}
        dayWidth={DAY_WIDTH}
        rowNameWidth={ROW_NAME_WIDTH}
        nonWorkingDaysMap={nonWorkingDaysMap}
        isNonWorkingDay={isShadedDay}
        getNonWorkingDayNames={getShadedDayNames}
        renderRows={renderRows}
        renderLabel={renderLabel}
      />
    </Paper>
    </GanttDndProvider>
  );
}
